import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { contactStore } from '../stores/contactStore';
import ContactCard from '../components/prospects/ContactCard';
import { useProfileRole } from '../hooks/useProfileRole';
import QuoteRequestList from '../components/prospects/QuoteRequestList';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AllianceQuoteRequest, QuoteRequest } from '../types/quote';
import QuoteRequestRespondModal from '../components/prospects/QuoteRequestRespondModal';
import { useQuoteRequests } from '../hooks/useQuoteRequests';
import ContactModal from '../components/prospects/ContactModal';
import { Contact } from '../types/contact';
import { decodeModalParams, cleanupModalParamsFromUrl } from '../utils/modalUrlParams';

const Prospects: React.FC = () => {
  const { t } = useTranslation('prospects');
  const { contacts, loading, fetchContacts } = contactStore();
  const { isRecruiter } = useProfileRole();

  const location = useLocation();
  const entityId = location.state?.selected_id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    allPendingQuoteRequests,
    quoteRequests,
    loading: quoteRequestsLoading,
  } = useQuoteRequests();

  const sharedQuoteIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (location.state?.selected_id) {
      sharedQuoteIdRef.current = location.state.selected_id;
    }
  }, [location.state?.selected_id]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState<
    QuoteRequest | AllianceQuoteRequest | null
  >(null);

  useEffect(() => {
    if (location.state?.selected_id) {
      sharedQuoteIdRef.current = location.state.selected_id;
    }
  }, [location.state?.selected_id]);

  // Restore modal from URL params after Stripe redirect
  useEffect(() => {
    const modalParams = decodeModalParams(searchParams);
    if (modalParams?.type === 'quoteRequest' && modalParams.quoteRequestId) {
      const quoteRequest =
        allPendingQuoteRequests.find(qr => qr.id === modalParams.quoteRequestId) ||
        quoteRequests.find(qr => qr.id === modalParams.quoteRequestId);
      if (quoteRequest) {
        setSelectedQuoteRequest(quoteRequest);
      }
    }
  }, [allPendingQuoteRequests, quoteRequests, searchParams]);

  useEffect(() => {
    if (isRecruiter || !location.state?.selected_id) return;
    const selectedId = String(location.state.selected_id);
    const quoteRequest =
      allPendingQuoteRequests.find(qr => qr.id === selectedId || qr.job?.id === selectedId) ||
      quoteRequests.find(qr => qr.id === selectedId || qr.job?.id === selectedId);
    if (quoteRequest) {
      setSelectedQuoteRequest(quoteRequest);
    }
  }, [isRecruiter, location.state?.selected_id, allPendingQuoteRequests, quoteRequests]);

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  useEffect(() => {
    if (!entityId) {
      setSelectedContact(null);
      return;
    }
    const isQuoteRequestOrJobId = [...allPendingQuoteRequests, ...quoteRequests].some(
      qr => qr.id === entityId || qr.job?.id === entityId
    );
    if (isQuoteRequestOrJobId) {
      setSelectedContact(null);
      return;
    }
    const foundContact = contacts.find(contact => contact.id === entityId);
    setSelectedContact(foundContact || null);
  }, [entityId, contacts, allPendingQuoteRequests, quoteRequests]);

  return (
    <>
      <div className="mb-8 flex flex-col gap-8 md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
        <div className="flex w-full flex-col pb-5 pt-4 md:w-2/3 md:px-1 md:pb-8 md:pr-8">
          <h1 className="text-5xl font-bold text-secondary">{t('title', 'Prospects')}</h1>
        </div>
        <div className="flex flex-col gap-8">
          {!isRecruiter && (
            <QuoteRequestList
              quoteRequests={allPendingQuoteRequests}
              loading={quoteRequestsLoading}
            />
          )}

          <div className="flex flex-col gap-2 rounded-2xl border-2 border-gray-300 bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-secondary">
              {t('dashboard:my_contacts')}
            </h2>
            {!loading &&
              (contacts && contacts.length > 0 ? (
                contacts.map((contact, i) => (
                  <div className="border-b last:border-b-0" key={i}>
                    <ContactCard key={contact.id} contact={contact} complete={true} />
                  </div>
                ))
              ) : (
                <div className="mb-4 text-sm text-gray-500">{t('prospects:noContacts')}</div>
              ))}
          </div>
        </div>
      </div>

      {selectedQuoteRequest && (
        <QuoteRequestRespondModal
          quoteRequest={selectedQuoteRequest}
          onClose={() => {
            setSelectedQuoteRequest(null);
            cleanupModalParamsFromUrl(searchParams, setSearchParams);
            navigate(location.pathname, { replace: true });
          }}
        />
      )}

      {selectedContact && (
        <ContactModal
          contact={selectedContact}
          onClose={() => {
            setSelectedContact(null);
            navigate(location.pathname, { replace: true });
          }}
        />
      )}
    </>
  );
};

export default Prospects;
